import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CARD } from '../ui';
import { API_BASE_URL } from '../config';
import { InvoiceActionButtons, InvoiceDetail, InvoiceStatus, dueLine } from './InvoiceParts';
import { formatMoney, formatMonth, plural } from './format';

const PAGE = 25;

const COLUMNS = [
  { key: 'company', label: 'Invoice', align: 'left' },
  { key: 'period', label: 'Month', align: 'left' },
  { key: 'total', label: 'Total', align: 'right' },
  { key: 'balance', label: 'Balance', align: 'right' },
  { key: 'status', label: 'Status', align: 'left' },
];

const SortIcon = ({ dir }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={`w-3 h-3 ${dir === 'asc' ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
  </svg>
);

// Every invoice in the current slice, sortable and searchable. A row opens to
// its lines, usage and payments, fetched on demand since the overview only
// carries totals. ``version`` changes whenever the page reloads its data, which
// throws away detail fetched before an action changed it.
const InvoiceTable = ({ rows, sort, onSort, query, onQuery, actions, liveKeys, scopeLabel, version }) => {
  const [limit, setLimit] = useState(PAGE);
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState({});

  useEffect(() => setDetails({}), [version]);
  useEffect(() => setLimit(PAGE), [rows.length, query]);

  useEffect(() => {
    if (expanded === null || details[expanded]) return undefined;
    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/api/billing/invoices/${expanded}`)
      .then((res) => !cancelled && setDetails((prev) => ({ ...prev, [expanded]: res.data })))
      .catch(() => !cancelled && setDetails((prev) => ({ ...prev, [expanded]: { error: true } })));
    return () => {
      cancelled = true;
    };
  }, [expanded, details]);

  const toggleSort = (key) =>
    onSort(sort.key === key ? { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'company' ? 'asc' : 'desc' });

  const shown = rows.slice(0, limit);

  return (
    <div className={`${CARD} overflow-hidden`}>
      <div className="px-6 py-5 border-b border-gray-700 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-white">Invoices</h2>
          <p className="text-sm text-gray-500 mt-1">
            {plural(rows.length, 'invoice')} · {scopeLabel}
          </p>
        </div>
        <input
          type="search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Search number or company"
          aria-label="Search invoices"
          className="w-full sm:w-64 px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      {rows.length === 0 ? (
        <p className="px-6 py-10 text-center text-sm text-gray-500">No invoices match.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[46rem] text-sm">
            <thead>
              <tr className="text-gray-400 border-b border-gray-700">
                {COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    aria-sort={sort.key === column.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    className={`px-4 first:pl-6 py-2 font-medium ${column.align === 'right' ? 'text-right' : 'text-left'}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleSort(column.key)}
                      className={`inline-flex items-center gap-1 bg-transparent hover:bg-transparent p-0 text-xs font-medium uppercase tracking-wide hover:text-white ${
                        sort.key === column.key ? 'text-purple-300' : 'text-gray-400'
                      }`}
                    >
                      {column.label}
                      {sort.key === column.key && <SortIcon dir={sort.dir} />}
                    </button>
                  </th>
                ))}
                <th scope="col" className="px-4 pr-6 py-2"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/60">
              {shown.map((invoice) => {
                const open = expanded === invoice.id;
                const detail = details[invoice.id];
                return (
                  <React.Fragment key={invoice.id}>
                    <tr className={open ? 'bg-gray-900/30' : 'hover:bg-gray-700/20'}>
                      <td className="pl-6 pr-4 py-3 min-w-[13rem]">
                        <button
                          type="button"
                          onClick={() => setExpanded(open ? null : invoice.id)}
                          aria-expanded={open}
                          className="bg-transparent hover:bg-transparent p-0 text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 rounded"
                        >
                          <span className="block font-medium text-white group-hover:text-purple-300 transition-colors">{invoice.company_name}</span>
                          <span className="block text-xs text-gray-500 whitespace-nowrap">{invoice.number} · {invoice.plan_name}</span>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-gray-300 whitespace-nowrap">{formatMonth(invoice.period)}</td>
                      <td className={`px-4 py-3 text-right tabular-nums ${invoice.status === 'void' ? 'text-gray-500 line-through' : 'text-white'}`}>
                        {formatMoney(invoice.total_cents)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-200">
                        {invoice.balance_cents ? formatMoney(invoice.balance_cents) : <span className="text-gray-600">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <InvoiceStatus invoice={invoice} />
                        <span className="block text-xs text-gray-500 mt-1 whitespace-nowrap">{dueLine(invoice)}</span>
                      </td>
                      <td className="px-4 pr-6 py-3">
                        <InvoiceActionButtons
                          invoice={invoice}
                          actions={actions}
                          liveSibling={liveKeys.has(`${invoice.company_id}:${invoice.period}`)}
                        />
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-gray-900/30">
                        <td colSpan={COLUMNS.length + 1} className="px-6 pb-5 pt-1">
                          {!detail && <p className="text-sm text-gray-500 py-3">Loading…</p>}
                          {detail?.error && <p className="text-sm text-red-300 py-3">Could not load this invoice.</p>}
                          {detail && !detail.error && <InvoiceDetail invoice={detail} />}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > limit && (
        <div className="px-6 py-3 border-t border-gray-700/60">
          <button
            type="button"
            onClick={() => setLimit((n) => n + PAGE)}
            className="bg-transparent hover:bg-gray-700/40 text-purple-300 hover:text-purple-200 px-2 py-1 -mx-2 rounded text-sm"
          >
            Show {Math.min(PAGE, rows.length - limit)} more of {rows.length - limit}
          </button>
        </div>
      )}
    </div>
  );
};

export default InvoiceTable;
